import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Box, Fade, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { IBizzaboSectionData, ICustomPrice, IPrice, IReducer } from '../../interface'
import { ProductContext } from './EducationLayout'
import AccordionContent from './AccordionContent'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import LazyLoad from 'react-lazyload'

interface IEducationSectionBlock {
  title: string
  descriptions?: string[]
  pricesData: IPrice[] | ICustomPrice[] | null
  sectionData: IBizzaboSectionData[]
}

const EducationSectionBlock = ({ title, descriptions, pricesData, sectionData }: IEducationSectionBlock) => {
  const [expanded, setExpanded] = useState<number | false>(0)
  const [image, setImage] = useState(0)
  const [loading, setLoading] = useState(true)
  const product = useContext(ProductContext)
  const isProductsCallFailure = useSelector((state: IReducer) => state.stripeProductsReducer.error)

  const handleExpandedChange = (panel: number) => {
    setImage(panel)
    setExpanded((prev) => (prev === panel ? false : panel))
  }

  const availableImages = useMemo(() => {
    const images: string[] = []
    if (pricesData?.length) {
      sectionData.forEach((data) => {
        if (data.sku) {
          const isPriceAvailable = (pricesData as IPrice[]).find((price) => data.sku === price.metadata?._sku)
          if (isPriceAvailable) {
            images.push(data.imageUrl)
          }
        } else {
          images.push(data.imageUrl)
        }
      })
    }
    return images
  }, [JSON.stringify(pricesData)])

  useEffect(() => {
    if (pricesData?.length || isProductsCallFailure) setLoading(false)
  }, [pricesData, isProductsCallFailure])

  return (
    <Stack component='section' sx={{ gap: 2 }}>
      <Box sx={{ width: '100%', textAlign: 'left' }}>
        <Typography variant='h2'>
          <FormattedMessage id={title} />
        </Typography>
      </Box>
      <Grid container spacing={{ xs: 0, md: 6 }}>
        <Grid item xs={12} md={6} sx={{ textAlign: 'left' }}>
          {descriptions?.map((description, index) => (
            <Typography key={index} sx={{ marginBottom: 2 }}>
              <FormattedMessage id={description} values={{ product }} />
            </Typography>
          ))}
          <Box
            marginY={{ xs: 4, md: 2 }}
            sx={{
              display: 'flex',
              flexBasis: { xs: '100%', md: '50%' },
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '16/9',
              position: 'relative'
            }}
          >
            <LazyLoad offset={300}>
              <Fade key={image} in>
                <Box
                  component='img'
                  sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'cover', borderRadius: 5 }}
                  src={availableImages[image]}
                />
              </Fade>
            </LazyLoad>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          {loading && <Skeleton width='100%' height={660} />}
          {isProductsCallFailure ? (
            <Typography>
              <FormattedMessage
                id='education.errorGettingProducts'
                values={{
                  url: (
                    <Link to='/store'>
                      <FormattedMessage id='nav.store' />
                    </Link>
                  )
                }}
              />
            </Typography>
          ) : (
            <>
              {pricesData?.map((item, index) => (
                <AccordionContent
                  key={index}
                  index={index}
                  item={item}
                  expanded={expanded}
                  handleExpandedChange={handleExpandedChange}
                  product={product}
                  sectionData={sectionData}
                />
              ))}
            </>
          )}
        </Grid>
      </Grid>
    </Stack>
  )
}

export default EducationSectionBlock
